import styled from '@emotion/styled';
import { Button } from 'components/atoms/button';
import { EnHeading } from 'components/atoms/enHeading';
import { ContentWrapper } from 'components/organisms/contentWrapper';
import React, { useEffect } from 'react';
import { TextSet } from 'components/molecules/textSet/index';
import { JaHeading } from 'components/atoms/jaHeading';
import { Text } from 'components/atoms/text/index';
import { ContentInner } from 'components/organisms/contentInner/index';
import ParticleImageManager from 'components/organisms/particleCanvas/ParticleImageManager';
import sizes from 'style/sizes';
import { mq } from 'style/variables';
import { graphql } from 'gatsby';
import SEO from 'util/seo';
import FadeUpSection from 'components/organisms/fadeUpSection';

interface IndexPageProps {
  data: {
    site: {
      siteMetadata: {
        title: string;
      };
    };
  };
}

const TitleSection = styled('div')`
  width: 100%;
  ${mq.onlypc} {
    padding-top: ${sizes.margin[180]};
    padding-bottom: ${sizes.margin[100]};
  }
  ${mq.onlysp} {
    padding-top: ${sizes.margin[155]};
    padding-bottom: ${sizes.margin[64]};
  }
`;

const ContactWrap = styled.div`
  padding-bottom: ${sizes.margin[180]};
  ${mq.onlysp} {
    padding-bottom: ${sizes.margin[120]};
  }
`;
const ContactItem = styled.div`
  &:not(:last-of-type) {
    padding-bottom: ${sizes.margin[180]};
  }
  ${mq.onlysp} {
    &:not(:last-of-type) {
      padding-bottom: ${sizes.margin[120]};
    }
  }
`;
const ContactTexts = styled(TextSet)`
  margin-bottom: 0;
  ${mq.onlysp} {
    margin-bottom: 0;
  }
  * + & {
    margin-top: ${sizes.margin[48]};
  }
  p + p {
    ${mq.onlysp} {
      margin-top: ${sizes.margin[48]};
    }
  }
  * + a {
    margin-top: 24px;
    ${mq.onlysp} {
      width: 251px;
      height: 47px;
      margin-left: auto;
      margin-right: auto;
    }
  }
`;
const ContactButtons = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-top: ${sizes.margin[56]};
  ${mq.onlysp} {
    margin-top: ${sizes.margin[44]};
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  > a {
    ${mq.onlysp} {
      width: 251px;
      height: 47px;
    }
    & + a {
      ${mq.onlypc} {
        margin-left: 15px;
      }
      ${mq.onlysp} {
        margin-top: 15px;
      }
    }
  }
`;

const Pages: React.FC<IndexPageProps> = () => {
  useEffect(() => {
    ParticleImageManager.loadAll().then(() => {
      ParticleImageManager.generateAll().then(() => {
        ParticleImageManager.start();
      });
    });

    return () => {
      ParticleImageManager.delete();
    };
  }, []);

  return (
    <>
      <SEO title={'Contact'} />
      <ContentWrapper>
        <TitleSection>
          <EnHeading>Contact</EnHeading>
        </TitleSection>
        <ContentInner>
          <ContactWrap>
            <ContactItem>
              <FadeUpSection>
                <ContactTexts>
                  <JaHeading>การติดต่อเกี่ยวกับงานหรือขอคำปรึกษา</JaHeading>
                  <Text>
                    โปรดติดต่อเราจากช่องทางนี้สำหรับการติดต่องานหรือขอคำปรึกษาเกี่ยวกับการออกแบบธุรกิจ (Business Design)
                    การปรับปรุงประสบการณ์ให้แก่ลูกค้า (CX Improvement) การออกแบบ MVV (MVV Design)
                    การออกแบบประสบการณ์ของพนักงาน (EX - Employee Experience Design) และ การสร้างแบรนด์ (Branding) <br />{' '}
                    ถึงแม้ว่าท่านอาจจะยังไม่ได้มีรายละเอียดที่เป็นรูปธรรมในขั้นตอนการวางแนวคิดเริ่มต้น
                    เราก็ยินดีที่จะให้คำปรึกษา
                  </Text>
                </ContactTexts>
                <ContactButtons>
                  <Button href={'https://4dsd.svy.ooo/ng/answers/28a8fd4eb5272b60eb8054b6ad2ce6/'} blank type={'fill'}>
                    Contact
                  </Button>
                </ContactButtons>
              </FadeUpSection>
            </ContactItem>

            <ContactItem>
              <FadeUpSection>
                <ContactTexts>
                  <JaHeading>เกี่ยวกับการรับสมัครงาน</JaHeading>
                  <Text>
                    ทางบริษัทเปิดรับสมัครบุคคลที่มีความสนใจในกิจกรรมและแนวทางการออกแบบของเราในหลากหลายตำแหน่ง ตั้งแต่ UX
                    Designer, Designer, Engineer ไปจนถึงฝ่ายบริหาร
                  </Text>
                  <ContactButtons>
                    <Button href={'/career'} blank>
                      Career
                    </Button>
                  </ContactButtons>
                </ContactTexts>
              </FadeUpSection>
            </ContactItem>

            <ContactItem>
              <FadeUpSection>
                <ContactTexts>
                  <JaHeading>ติดต่อสอบถามเกี่ยวกับการประชาสัมพันธ์และข่าวสาร</JaHeading>
                  <Text>
                    โปรดติดต่อเราผ่านช่องทางนี้ เพื่อสอบถามเกี่ยวกับการเผยแพร่ข่าวสาร (Press Release), การขอสัมภาษณ์,
                    การขอใช้พื้นที่สำนักงาน หรือความคิดเห็นอื่น ๆ ที่มีต่อบริษัทหรือทีมงานของเรา
                  </Text>
                </ContactTexts>
                <ContactButtons>
                  <Button href={'https://4dsd.svy.ooo/ng/answers/5712ccdddf205fb616b9d0107242a0/'} blank type={'fill'}>
                    Contact
                  </Button>
                </ContactButtons>
              </FadeUpSection>
            </ContactItem>
          </ContactWrap>
        </ContentInner>
      </ContentWrapper>
    </>
  );
};

export default Pages;

export const pageQuery = graphql`
  query ContactTh {
    site {
      siteMetadata {
        title
      }
    }
  }
`;
